/** Sesiunea curentă: încarcă /me, oferă guard + logout. */
import { createContext, useCallback, useContext } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useMe } from '../api/hooks';
import { signOut } from '../api/auth';
import type { Me } from '../api/types';
import { Logo } from '../components/Logo';

interface SessionCtx {
  user: Me;
  logout: () => Promise<void>;
}

const Ctx = createContext<SessionCtx | null>(null);

function Splash() {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, opacity: 0.85 }}>
        <div style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
          <Logo withWordmark />
        </div>
        <span style={{ color: 'var(--muted)', fontSize: 13 }}>Se încarcă studioul…</span>
      </div>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useMe();
  const location = useLocation();
  const qc = useQueryClient();

  const logout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      qc.clear();
      window.location.href = '/login';
    }
  }, [qc]);

  if (isLoading) return <Splash />;
  if (isError || !data) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  return <Ctx.Provider value={{ user: data, logout }}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession trebuie folosit sub RequireAuth');
  return ctx;
}
